import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SectionTitle, Loading, FieldRule, Placeholder, StatusChip } from "@/components/common";
import { ReferenceGallery } from "@/components/ReferenceGallery";
import { useAuth } from "@/context/AuthContext";
import { getActiveJob, setActiveJob } from "@/lib/activeJob";
import { ScanLine, Camera, Upload, CheckCircle2, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const ANGLE_LABEL = {
  front: "Front", left: "Left", rear: "Rear", right: "Right", service_areas: "Service Area",
  water_source: "Water Source", electrical_hazards: "Electrical Concerns",
  landscaping_protection: "Landscaping / Protection", existing_damage: "Existing Damage",
  specialty_conditions: "Specialty Condition",
};

const HAZARD_OPTS = ["electrical", "unstable surfaces", "access concerns", "fragile materials", "pets", "landscaping/property protection", "other"];
const PROTECTION_OPTS = ["landscaping", "electrical fixtures", "windows/doors", "runoff concerns", "sensitive surfaces", "other"];
const SPECIALTY_EX = ["oil", "grease", "tar", "rust", "oxidation", "efflorescence", "battery staining", "irrigation/mineral staining", "gum", "other unusual contamination"];

const AREA_OPTS = ["Driveway", "Walkways", "Patio / Deck", "Porch", "Siding / Home Exterior", "Exterior windows", "Gutters", "Fencing", "Roof", "Garage door", "Pool deck", "Vehicle(s)", "Exterior Structure / Detached Structure", "Other"];
const DAMAGE_OPTS = ["Cracked", "Chipped", "Rust / oxidation", "Loose / unstable", "Stained / discolored", "Peeling / deteriorated", "Previously repaired", "Other"];
const WATER_LOC = ["Front", "Rear", "Left Side", "Right Side", "Other"];
const FLOW_OPTS = ["Verified sufficient", "Low flow", "Unable to verify", "Customer confirmed", "Other"];
const UTIL_OPTS = ["None", "Exposed outlet/wiring", "Damaged cover", "Nearby utility equipment", "Other"];

const CONDITION = [
  ["normal", "Normal", "0%", "Light dirt, dust, minor spotting; regularly maintained with limited buildup."],
  ["heavy", "Heavy", "+15%", "Clearly visible widespread dirt, algae, grime or organic buildup; overdue for cleaning."],
  ["severe", "Severe", "+25%", "Thick or widespread buildup, significant organic growth, extensive staining, prolonged neglect."],
  ["extreme", "Extreme", "Manager Review", "Near-full contamination/growth, years of neglect, major unknowns — professional review required."],
];
const ACCESS = [
  ["normal", "Normal", "Included", "Standard ground-level access, no obstacles."],
  ["difficult", "Difficult", "+10%", "Tight spaces, obstacles, or extra setup to reach the work area."],
  ["elevated", "Elevated / High", "+25%", "Height, steep grade, or ladder work increasing difficulty."],
  ["specialized", "Specialized", "Manager Review", "Lifts, roof access, confined or hazardous conditions — review required."],
];
const SPECIALTY = [
  ["none", "None", "$0", "No specialty contamination present."],
  ["small", "Small / Localized", "+$50", "One small, contained specialty spot (e.g. a single oil stain)."],
  ["moderate", "Moderate", "+$100", "Several areas or a moderate specialty condition."],
  ["extensive", "Extensive", "+$150 / Review", "Widespread specialty contamination — may require custom review. No guaranteed removal."],
];

const STEPS = ["Property", "Surface", "Condition", "Access", "Specialty", "Damage", "Hazards", "Water/Utilities", "Protection", "Photos", "Review"];

export default function ShadowScan() {
  const { user } = useAuth();
  const canManageRefs = user.role === "owner" || user.role === "manager";
  const [params] = useSearchParams();
  const [angles, setAngles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [jobServices, setJobServices] = useState([]);
  const [cat, setCat] = useState(null);
  const [step, setStep] = useState(0);
  const [photos, setPhotos] = useState({});
  const [busy, setBusy] = useState("");
  const [saving, setSaving] = useState(false);
  const [d, setD] = useState({
    service_category: "", measure: "", size_key: "",
    surf_incl: [], surf_excl: [], surf_incl_note: "", surf_excl_note: "", surface_unknown: false,
    condition: "normal", access: "normal", specialty: "none",
    damage: [], damage_note: "", existing_damage: "", hazards: [], hazard_note: "", protection: [], protection_note: "",
    water_source: { available: true, location: "", location_detail: "", flow: "", flow_detail: "", utility: "", utility_detail: "" },
    notes: "",
  });

  useEffect(() => {
    if (!jobId) { setJobServices([]); return; }
    api.get(`/jobs/${jobId}/services`).then(({ data }) => setJobServices(data.job_services || []));
  }, [jobId]);

  // NEXT/BACK must open the new step at its top (reset window + any nested scroll container).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    const el = document.querySelector('[data-testid="scan-panel"]');
    if (el) { el.scrollTop = 0; el.scrollIntoView({ block: "start", behavior: "auto" }); }
  }, [step]);

  useEffect(() => {
    api.get("/scan/required-angles").then(({ data }) => setAngles(data.required));
    api.get("/jobs").then(({ data }) => {
      setJobs(data);
      const ctx = params.get("job");
      const saved = getActiveJob(user?.id);
      if (ctx && data.find((j) => j.id === ctx)) setJobId(ctx);
      else if (saved && data.find((j) => j.id === saved)) setJobId(saved);
      // otherwise leave unselected → main/select-job state (no auto-pick, no cross-user carryover)
    });
    api.get("/pricing/catalog").then(({ data }) => setCat(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickJob = (id) => { setJobId(id); if (id) setActiveJob(user?.id, id); };

  const upd = (patch) => setD((s) => ({ ...s, ...patch }));
  const toggleArr = (key, val) => setD((s) => ({ ...s, [key]: s[key].includes(val) ? s[key].filter((x) => x !== val) : [...s[key], val] }));

  const upload = async (angle, file) => {
    if (!file) return;
    setBusy(angle);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/upload?job_id=${jobId}&tag=scan_${angle}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => ({ ...p, [angle]: data.id }));
      toast.success(`${ANGLE_LABEL[angle]} uploaded`);
    } catch { toast.error("Upload failed"); } finally { setBusy(""); }
  };

  const save = async () => {
    if (!jobId) { toast.error("Select a job"); return; }
    if (saving) return;
    setSaving(true);
    try {
      await api.post(`/jobs/${jobId}/scan`, {
        photos, condition: d.condition, access: d.access, specialty: d.specialty,
        service_category: "", measure: null, size_key: "",
        surfaces_included: [...d.surf_incl, d.surf_incl_note.trim()].filter(Boolean).join(", "),
        surfaces_excluded: [...d.surf_excl, d.surf_excl_note.trim()].filter(Boolean).join(", "),
        surface_unknown: d.surface_unknown,
        hazards: d.hazards, protection: d.protection,
        hazard_details: d.hazard_note.trim(), protection_details: d.protection_note.trim(),
        water_source: d.water_source,
        existing_damage: [...d.damage, d.damage_note.trim()].filter(Boolean).join(", "), notes: d.notes,
      });
      toast.success("Shadow Scan saved → Estimator now sources these classifications");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save scan");
    } finally { setSaving(false); }
  };

  if (angles.length === 0 || !cat) return <Loading label="Loading scan" />;
  const done = Object.keys(photos).length;
  const catList = [...Object.entries(cat.measured_categories).map(([k, v]) => ({ k, label: v.label })), ...Object.entries(cat.size_categories).map(([k, v]) => ({ k, label: v.label }))];

  const OptionGrid = ({ opts, value, onSelect, testid }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={testid}>
      {opts.map(([k, label, tag, desc]) => (
        <button key={k} onClick={() => onSelect(k)} data-testid={`${testid}-${k}`}
          className={`sg-card-elev p-4 text-left ${value === k ? "tile-active" : ""}`}>
          <div className="flex items-center justify-between">
            <div className="font-head font-bold">{label}</div>
            <div className="text-xs font-mono" style={{ color: k === value ? "var(--accent)" : "var(--txt2)" }}>{tag}</div>
          </div>
          {desc && <div className="text-xs mt-1 leading-snug" style={{ color: "var(--txt2)" }}>{desc}</div>}
        </button>
      ))}
    </div>
  );

  // Multi-select chip group.
  const Chips = ({ opts, selected, onToggle, tone = "var(--accent)", testid }) => (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {opts.map((o) => {
        const on = selected.includes(o);
        return (
          <button key={o} data-testid={`${testid}-${o}`} onClick={() => onToggle(o)}
            className="chip" style={{ cursor: "pointer", background: on ? tone : "var(--bg-elev)", color: on ? "#fff" : "var(--txt2)", borderColor: on ? tone : "var(--brd)" }}>{o}</button>
        );
      })}
    </div>
  );
  // Single-select chip group.
  const PickChips = ({ opts, value, onSelect, testid }) => (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {opts.map((o) => (
        <button key={o} data-testid={`${testid}-${o}`} onClick={() => onSelect(o)}
          className="chip" style={{ cursor: "pointer", background: value === o ? "var(--accent)" : "var(--bg-elev)", color: value === o ? "#fff" : "var(--txt2)", borderColor: value === o ? "var(--accent)" : "var(--brd)" }}>{o}</button>
      ))}
    </div>
  );

  return (
    <div className="max-w-6xl">
      <SectionTitle sub="Guided inspection — Shadow Scan is the source of truth for the Estimator">Shadow Scan</SectionTitle>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col gap-4">
          <div className="sg-card p-4 flex items-center gap-3">
            <ScanLine style={{ color: "var(--accent)" }} />
            <select className="sg-input" value={jobId} onChange={(e) => pickJob(e.target.value)} data-testid="scan-job">
              <option value="">— Select a job —</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.title || j.id.slice(0, 8)}</option>)}
            </select>
            <span className="chip" style={{ background: "var(--bg-elev)", color: "var(--txt2)", borderColor: "var(--brd)" }}>{done}/{angles.length} photos</span>
          </div>

          {/* Step chips */}
          <div className="flex gap-1 overflow-x-auto pb-1" data-testid="scan-steps">
            {STEPS.map((s, i) => (
              <button key={s} onClick={() => setStep(i)} data-testid={`scan-step-${i}`}
                className="px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap"
                style={{ background: i === step ? "var(--accent)" : "var(--bg-elev)", color: i === step ? "#fff" : "var(--txt2)" }}>
                {String.fromCharCode(65 + i)}. {s}
              </button>
            ))}
          </div>

          <div className="sg-card p-5 min-h-[320px]" data-testid={`scan-panel-${step}`}>
            {step === 0 && (
              <div className="flex flex-col gap-3">
                <div className="font-head text-lg font-bold">A · Property & Scope</div>
                <p className="text-xs" style={{ color: "var(--txt2)" }}>Scope comes from the job's structured services. This scan documents property-level condition/access for pricing.</p>
                <div className="flex flex-col gap-2" data-testid="scan-job-services">
                  {jobServices.length === 0
                    ? <div className="text-sm p-3 rounded-lg" style={{ background: "rgba(245,158,11,0.1)", color: "#F59E0B" }} data-testid="scan-no-services">No services on this job yet — add services in the Job Workspace before scanning.</div>
                    : jobServices.map((s) => (
                      <div key={s.job_service_id} className="sg-card-elev p-3 flex items-center justify-between" data-testid={`scan-service-${s.job_service_id}`}>
                        <span className="font-semibold text-sm">{s.label}{s.quantity_index ? ` (Vehicle ${s.quantity_index})` : ""}</span>
                        <span className="text-xs" style={{ color: "var(--txt2)" }}>{s.size_key || (s.measure ? `${s.measure} ft` : "")}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
            {step === 1 && (
              <div className="flex flex-col gap-4">
                <div className="font-head text-lg font-bold">B · Surface</div>
                <div>
                  <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Areas included</label>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="scan-included">
                    {AREA_OPTS.map((a) => {
                      const on = d.surf_incl.includes(a);
                      return (
                        <button key={a} data-testid={`scan-incl-${a}`} onClick={() => upd({ surf_incl: on ? d.surf_incl.filter((x) => x !== a) : [...d.surf_incl, a] })}
                          className="chip" style={{ cursor: "pointer", background: on ? "var(--accent)" : "var(--bg-elev)", color: on ? "#fff" : "var(--txt2)", borderColor: on ? "var(--accent)" : "var(--brd)" }}>{a}</button>
                      );
                    })}
                  </div>
                  <input className="sg-input mt-2" placeholder="Additional notes (optional)" value={d.surf_incl_note} data-testid="scan-included-note" onChange={(e) => upd({ surf_incl_note: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Areas excluded</label>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="scan-excluded">
                    {AREA_OPTS.map((a) => {
                      const on = d.surf_excl.includes(a);
                      return (
                        <button key={a} data-testid={`scan-excl-${a}`} onClick={() => upd({ surf_excl: on ? d.surf_excl.filter((x) => x !== a) : [...d.surf_excl, a] })}
                          className="chip" style={{ cursor: "pointer", background: on ? "var(--danger)" : "var(--bg-elev)", color: on ? "#fff" : "var(--txt2)", borderColor: on ? "var(--danger)" : "var(--brd)" }}>{a}</button>
                      );
                    })}
                  </div>
                  <input className="sg-input mt-2" placeholder="Additional notes (optional) — describe any 'Other' exclusion" value={d.surf_excl_note} data-testid="scan-excluded-note" onChange={(e) => upd({ surf_excl_note: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 text-sm" data-testid="scan-unknown-surface">
                  <input type="checkbox" checked={d.surface_unknown} onChange={(e) => upd({ surface_unknown: e.target.checked })} />
                  Unknown / unrecognized surface — flag for review (do not spray)
                </label>
              </div>
            )}
            {step === 2 && (
              <div className="flex flex-col gap-3">
                <div className="font-head text-lg font-bold">C · Condition</div>
                <OptionGrid opts={CONDITION} value={d.condition} onSelect={(k) => upd({ condition: k })} testid="scan-condition" />
                {d.condition === "extreme" && <StatusChip status="warning" label="Extreme — Custom / Manager Review (no automatic surcharge)" />}
                <ReferenceGallery classificationType="condition" options={CONDITION} canManage={canManageRefs} />
              </div>
            )}
            {step === 3 && (
              <div className="flex flex-col gap-3">
                <div className="font-head text-lg font-bold">D · Access</div>
                <OptionGrid opts={ACCESS} value={d.access} onSelect={(k) => upd({ access: k })} testid="scan-access" />
                {d.access === "specialized" && <StatusChip status="warning" label="Specialized Access — Custom / Manager Review" />}
                <ReferenceGallery classificationType="access" options={ACCESS} canManage={canManageRefs} />
              </div>
            )}
            {step === 4 && (
              <div className="flex flex-col gap-3">
                <div className="font-head text-lg font-bold">E · Specialty Conditions</div>
                <OptionGrid opts={SPECIALTY} value={d.specialty} onSelect={(k) => upd({ specialty: k })} testid="scan-specialty" />
                <div className="text-xs" style={{ color: "var(--txt2)" }}>Examples: {SPECIALTY_EX.join(", ")}</div>
                <ReferenceGallery classificationType="specialty" options={SPECIALTY} canManage={canManageRefs} />
              </div>
            )}
            {step === 5 && (
              <div className="flex flex-col gap-3">
                <div className="font-head text-lg font-bold">F · Existing Damage</div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Observed damage (select all that apply)</label>
                <Chips opts={DAMAGE_OPTS} selected={d.damage} onToggle={(v) => toggleArr("damage", v)} tone="var(--danger)" testid="scan-damage" />
                <label className="text-xs uppercase tracking-wider mt-1" style={{ color: "var(--txt2)" }}>
                  Additional details {d.damage.includes("Other") ? "(required for Other)" : "(optional)"}
                </label>
                <input className="sg-input" placeholder="e.g. hairline crack along the left column" value={d.damage_note} data-testid="scan-damage-note" onChange={(e) => upd({ damage_note: e.target.value })} />
                <div className="text-xs" style={{ color: "var(--txt2)" }}>Capture an "Existing Damage" photo in step J. Customer acknowledgment recorded at authorization.</div>
              </div>
            )}
            {step === 6 && (
              <div className="flex flex-col gap-3">
                <div className="font-head text-lg font-bold">G · Hazards</div>
                <Chips opts={HAZARD_OPTS} selected={d.hazards} onToggle={(v) => toggleArr("hazards", v)} testid="scan-hazards" />
                {d.hazards.length > 0 && (
                  <>
                    <label className="text-xs uppercase tracking-wider mt-1" style={{ color: "var(--txt2)" }}>
                      Hazard details {d.hazards.includes("other") ? "(required for Other)" : "(optional)"}
                    </label>
                    <input className="sg-input" placeholder="e.g. loose exterior outlet near rear patio" value={d.hazard_note} data-testid="scan-hazard-note" onChange={(e) => upd({ hazard_note: e.target.value })} />
                  </>
                )}
              </div>
            )}
            {step === 7 && (
              <div className="flex flex-col gap-4">
                <div className="font-head text-lg font-bold">H · Water / Utilities</div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={d.water_source.available} data-testid="scan-water-available" onChange={(e) => upd({ water_source: { ...d.water_source, available: e.target.checked } })} />
                  Exterior water source available
                </label>
                <div>
                  <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Water-source location</label>
                  <div className="mt-2"><PickChips opts={WATER_LOC} value={d.water_source.location} onSelect={(v) => upd({ water_source: { ...d.water_source, location: v } })} testid="scan-water-location" /></div>
                  {d.water_source.location === "Other" && <input className="sg-input mt-2" placeholder="Describe location" value={d.water_source.location_detail} data-testid="scan-water-location-detail" onChange={(e) => upd({ water_source: { ...d.water_source, location_detail: e.target.value } })} />}
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Flow verification</label>
                  <div className="mt-2"><PickChips opts={FLOW_OPTS} value={d.water_source.flow} onSelect={(v) => upd({ water_source: { ...d.water_source, flow: v } })} testid="scan-water-flow" /></div>
                  {d.water_source.flow === "Other" && <input className="sg-input mt-2" placeholder="Flow notes" value={d.water_source.flow_detail} data-testid="scan-water-flow-detail" onChange={(e) => upd({ water_source: { ...d.water_source, flow_detail: e.target.value } })} />}
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Electrical / utility concern</label>
                  <div className="mt-2"><PickChips opts={UTIL_OPTS} value={d.water_source.utility} onSelect={(v) => upd({ water_source: { ...d.water_source, utility: v } })} testid="scan-water-utility" /></div>
                  {["Other", "Nearby utility equipment"].includes(d.water_source.utility) && <input className="sg-input mt-2" placeholder="Describe the concern" value={d.water_source.utility_detail} data-testid="scan-water-utility-detail" onChange={(e) => upd({ water_source: { ...d.water_source, utility_detail: e.target.value } })} />}
                </div>
              </div>
            )}
            {step === 8 && (
              <div className="flex flex-col gap-3">
                <div className="font-head text-lg font-bold">I · Property Protection Requirements</div>
                <Chips opts={PROTECTION_OPTS} selected={d.protection} onToggle={(v) => toggleArr("protection", v)} testid="scan-protection" />
                {d.protection.length > 0 && (
                  <>
                    <label className="text-xs uppercase tracking-wider mt-1" style={{ color: "var(--txt2)" }}>
                      Additional details {d.protection.includes("other") ? "(required for Other)" : "(optional)"}
                    </label>
                    <input className="sg-input" placeholder="e.g. cover rear flower beds and koi pond" value={d.protection_note} data-testid="scan-protection-note" onChange={(e) => upd({ protection_note: e.target.value })} />
                  </>
                )}
              </div>
            )}
            {step === 9 && (
              <div className="flex flex-col gap-3">
                <div className="font-head text-lg font-bold">J · Required Photos</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="scan-angles">
                  {angles.map((a) => (
                    <label key={a} className={`sg-card-elev p-4 cursor-pointer flex flex-col gap-2 ${photos[a] ? "tile-active" : ""}`} data-testid={`scan-angle-${a}`}>
                      {photos[a] ? <CheckCircle2 size={20} style={{ color: "#10B981" }} /> : <Camera size={20} style={{ color: "var(--txt2)" }} />}
                      <span className="font-head font-bold text-sm">{ANGLE_LABEL[a]}</span>
                      <span className="text-xs flex items-center gap-1" style={{ color: photos[a] ? "#10B981" : "var(--accent)" }}><Upload size={12} /> {busy === a ? "Uploading…" : photos[a] ? "Captured" : "Add"}</span>
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(a, e.target.files[0])} />
                    </label>
                  ))}
                </div>
              </div>
            )}
            {step === 10 && (
              <div className="flex flex-col gap-3" data-testid="scan-review">
                <div className="font-head text-lg font-bold">K · Scan Review</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <Sum k="Services" v={jobServices.length ? `${jobServices.length} on job` : "none"} />
                  <Sum k="Condition" v={d.condition} />
                  <Sum k="Access" v={d.access} />
                  <Sum k="Specialty" v={d.specialty} />
                  <Sum k="Photos" v={`${done}/${angles.length}`} />
                  <Sum k="Damage" v={d.damage.join(", ") || "none"} />
                  <Sum k="Hazards" v={d.hazards.join(", ") || "none"} />
                  <Sum k="Protection" v={d.protection.join(", ") || "none"} />
                </div>
                {(d.condition === "extreme" || d.access === "specialized" || d.specialty === "extensive") && (
                  <StatusChip status="warning" label="Manager / Custom Review required" />
                )}
                {d.surface_unknown && <StatusChip status="warning" label="Unknown surface flagged — do not spray until identified" />}
                <button className="sg-btn sg-btn-accent w-full mt-2" onClick={save} disabled={saving} data-testid="save-scan-btn">{saving ? "Saving…" : "Complete & Save Shadow Scan"}</button>
              </div>
            )}
          </div>

          {/* Sticky action bar — Back/Next always reachable without scrolling (sits above bottom app nav) */}
          <div className="sticky z-30 sg-card px-4 py-3 flex items-center justify-between gap-3"
            style={{ bottom: "84px", boxShadow: "0 -6px 20px rgba(0,0,0,0.25)" }} data-testid="scan-nav-bar">
            <button className="sg-btn" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="scan-prev"><ChevronLeft size={16} /> Back</button>
            <span className="text-xs font-mono" style={{ color: "var(--txt2)" }}>{String.fromCharCode(65 + step)} · {STEPS[step]} · {step + 1}/{STEPS.length}</span>
            {step < STEPS.length - 1 ? (
              <button className="sg-btn sg-btn-accent" onClick={() => setStep((s) => s + 1)} data-testid="scan-next">Next <ChevronRight size={16} /></button>
            ) : (
              <button className="sg-btn sg-btn-accent" onClick={save} disabled={saving} data-testid="scan-nav-save">{saving ? "Saving…" : "Save"}</button>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <FieldRule />
          <div className="sg-card p-5">
            <div className="font-head text-lg font-bold mb-2">Technical SOPs</div>
            <Placeholder label="TRAINER VALIDATION REQUIRED" />
            <div className="text-xs mt-2" style={{ color: "var(--txt2)" }}>Chemical mixes, dwell times, pressure settings & roof procedures are not defined here until trainer-validated.</div>
            <div className="mt-3"><Placeholder label="AI classification — Future Phase" /></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Sum({ k, v }) {
  return (
    <div className="sg-card-elev p-3">
      <div className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>{k}</div>
      <div className="font-semibold capitalize">{v || "—"}</div>
    </div>
  );
}
